import { last } from 'lodash';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { IContract, IProject, useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { useMutationDeleteSubProgram } from 'api/mutations/deleteSubProgram.mutation';
import { useQueryGetGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList-2.query';

import { ProjectTreeNode, findeNodeByUUID } from 'components/common/TreeProgramFilter/ProjectTree/projectTree.utils';
import { CustomizedConfirmDeleteModal } from 'components/common/index';

import { initialFiltersState } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';

interface Props {
    handleModalClose: () => void;
    isShowModal: boolean;
    callback?: (project: IProject | null, contract: IContract | null) => void;
}

export const ConfirmDeleteSubProgramModal = ({ handleModalClose, isShowModal, callback }: Props) => {
    const { setVersion } = useVersionStore();
    const { project, contract, update } = useProjectTreeStore();
    const { data: projectHierarchyList } = useQueryGetGeneralHierarchyList();
    const queryClient = useQueryClient();
    const updateReplace = useGlobalFilterDataStore((state) => state.updateReplace);
    const { mutate, isLoading } = useMutationDeleteSubProgram();
    const { handleSnackBar } = useCustomSnackBar();

    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleDeleteSubProgram = () => {
        if (contract) {
            const projectNode =
                (projectHierarchyList && project && findeNodeByUUID(projectHierarchyList, project?.uuid)) || null;
            const subProgramList = projectNode?.children;
            if (subProgramList && subProgramList.length === 1) {
                handleModalClose();
                handleSnackBar(`Can't delete sub-program. Project must have at least one sub-program`, 'error');
            } else {
                mutate(
                    { id: contract?.id },
                    {
                        onSuccess: () => {
                            handleSnackBar('Sub Program deleted successfully', 'success');
                            Promise.all([
                                queryClient.invalidateQueries('getGeneralHierarchyList'),
                                queryClient.invalidateQueries('GetProgramGeneralHierarchyList'),
                            ]).then(() => {
                                const newData = queryClient.getQueryData<ProjectTreeNode>('getGeneralHierarchyList');
                                const projectNode =
                                    (newData && project && findeNodeByUUID(newData, project?.uuid)) || null;
                                if (projectNode) {
                                    const lastContract: ProjectTreeNode | undefined = last(projectNode?.children);
                                    update({
                                        contract: lastContract
                                            ? {
                                                  id: lastContract.id,
                                                  title: lastContract.name,
                                                  uuid: lastContract.uuid,
                                              }
                                            : null,
                                    });

                                    if (callback) {
                                        callback(
                                            project ? { ...project } : null,
                                            lastContract
                                                ? {
                                                      id: lastContract.id,
                                                      title: lastContract.name,
                                                      uuid: lastContract.uuid,
                                                  }
                                                : null,
                                        );
                                    }
                                }
                            });

                            setVersion(null);
                            updateReplace(initialFiltersState);

                            sendEventWithDimensions({
                                category: 'Program Library',
                                action: 'Delete Sub-Program',
                                label: contract?.title,
                            });
                        },
                        onError: (error) => {
                            DEFAULT_ERROR({ error, handleError: handleSnackBar });
                        },
                        onSettled: () => handleModalClose(),
                    },
                );
            }
        }
    };

    return (
        <CustomizedConfirmDeleteModal
            onConfirm={handleDeleteSubProgram}
            hideModal={handleModalClose}
            isShowModal={isShowModal}
            isLoading={isLoading}
            text={`Are you sure you want to delete this sub-program (${contract?.title})?`}
        />
    );
};
